import {
  SwipeableProgressRatio,
  SwipeableSlider,
  TypographyV3,
  useGoToSwipeableSlide,
  useSwipeable,
} from '@jouzen/ecom-components';
import { cx } from 'class-variance-authority';
import { useTranslations } from 'next-intl';
import type { ComponentPropsWithoutRef } from 'react';
import { Fragment } from 'react';

import Image from '@/app/components/Image';

import { galleryImages } from '../_data/galleryImages';

const SliderButton = ({
  className = '',
  onClick,
  disabled,
  children,
}: ComponentPropsWithoutRef<'button'>): JSX.Element => (
  <button
    disabled={disabled}
    className={cx(
      'absolute flex size-16 items-center justify-center rounded-full',
      'border-2 border-solid border-sandstone-200',
      'text-heading-base text-sandstone-200',
      'lg:size-24 lg:text-heading-2xl',
      'hover:bg-sandstone-200 hover:bg-opacity-20',
      'disabled:opacity-20',
      className,
    )}
    onClick={onClick}
  >
    {children}
  </button>
);

const Gallery = (): JSX.Element => {
  const t = useTranslations();
  const { canScrollNext, canScrollPrev } = useSwipeable();
  const { activeSlide, goToSlide } = useGoToSwipeableSlide();

  return (
    <>
      <div className="relative inset-0 z-10 size-full">
        <div className="absolute bottom-36 z-10 hidden w-full md:block lg:bottom-48">
          <SliderButton
            disabled={!canScrollPrev}
            className="left-48"
            onClick={() => goToSlide(activeSlide - 1)}
          >
            &larr;
          </SliderButton>
          <SliderButton
            disabled={!canScrollNext}
            className="right-48"
            onClick={() => goToSlide(activeSlide + 1)}
          >
            &rarr;
          </SliderButton>
        </div>
        <div className="flex flex-col-reverse">
          <SwipeableProgressRatio className="mt-3 text-blue-200" />
          <SwipeableSlider slideClassName="snap-center">
            {galleryImages.map((image, idx) => (
              <Fragment key={image.src}>
                <figure className="relative h-[60vw] max-h-[740px] w-[70vw] max-w-screen-xxl md:w-[80vw]">
                  <Image
                    className="w-full object-cover"
                    fill
                    localizedAlt={image.alt}
                    sizes="80vw"
                    src={image.src}
                  />
                </figure>
                <TypographyV3
                  Element="figcaption"
                  weight="normal"
                  color="dark"
                  className={cx(
                    'absolute bottom-0 right-0 z-20 float-right mt-6 inline transition-opacity',
                    {
                      'opacity-0': activeSlide !== idx,
                    },
                  )}
                >
                  {t(image.caption)}
                </TypographyV3>
              </Fragment>
            ))}
          </SwipeableSlider>
        </div>
      </div>
    </>
  );
};

export default Gallery;
